import { UpgradeOption, UpgradeOptions, UseDesktopFlight, UseDesktopFlightWithPopup } from '../../types';
import { useDesktopFlight } from '../../hooks';
import { useToggleable } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { FareFamilyOptionsType, FareFeaturePaymentType } from '@websky/graphql';

const OFFER_UPGRADE_IF_UNAVAILABLE = [
	FareFamilyOptionsType.Exchangeable,
	FareFamilyOptionsType.Baggage,
	FareFamilyOptionsType.Refundable
];

export const useDesktopFlightWithPopup: UseDesktopFlightWithPopup = (props, defaultModalState = false) => {
	const { isOpen: isModalOpen, open: openModal, close: closeModal } = useToggleable(defaultModalState);
	const { isOpen: isComparisonOpen, setOpen: setIsComparisonOpen } = useToggleable(false);

	const upgradeOptions: UpgradeOptions = new Map();
	const desktopFlightData = useDesktopFlight(props);
	const { t } = useTranslation('FareGroupUpgradeOptions');

	const tryTranslateWithFareKey = (fareFamilyId: string, key: string, defaultValue?: string) => {
		return defaultValue ? t([`${key}_${fareFamilyId}`, key], { defaultValue }) : t([`${key}_${fareFamilyId}`, key]);
	};

	// wrap openModal with function, which implement interface of getOnFareClickHandler
	// but call openModal instead of previous logic
	const handleModalOpen: ReturnType<UseDesktopFlight>['getFareGroupTabHandler'] = serviceClass => openModal;

	props.flight.pricesForFareGroups.forEach((priceForFareGroup, index) => {
		if (!priceForFareGroup.prices) {
			return;
		}

		const nextFare = props.flight.pricesForFareGroups[(index as number) + 1];

		if (!nextFare || desktopFlightData.lastSelectedFare) {
			return;
		}

		// collect all unavailable options on current fare
		const fareUnavailableServicesTypes = priceForFareGroup.fareFamily?.options
			?.filter(option => option.availability === FareFeaturePaymentType.NotAvailable)
			?.map(option => option.type);

		// collect all available options on next fare
		// options which affected, listed in OFFER_UPGRADE_IF_UNAVAILABLE array
		const availableOptionsOnNextFare: FareFamilyOptionsType[] = Array.from(
			new Set(
				nextFare.fareFamily?.options
					.filter(
						option =>
							option.availability !== FareFeaturePaymentType.NotAvailable &&
							fareUnavailableServicesTypes?.includes(option.type) &&
							OFFER_UPGRADE_IF_UNAVAILABLE.includes(option.type) &&
							!priceForFareGroup.fareFamily?.options?.some(
								currentOption =>
									currentOption.type === option.type &&
									currentOption.availability === option.availability
							)
					)
					.map(option => option.type)
			)
		);

		if (availableOptionsOnNextFare.length) {
			const fareFamilyId = priceForFareGroup.fareFamily.id;
			const fareKey =
				availableOptionsOnNextFare[0] === FareFamilyOptionsType.Baggage ? 'NeedMasculine' : 'NeedFeminine';

			const upgradeOffer: string =
				tryTranslateWithFareKey(fareFamilyId, fareKey) +
				availableOptionsOnNextFare
					.map(type => {
						switch (type) {
							case FareFamilyOptionsType.Baggage:
								return tryTranslateWithFareKey(fareFamilyId, 'BaggageOption');
							case FareFamilyOptionsType.Exchangeable:
								return tryTranslateWithFareKey(fareFamilyId, 'ExchangeOption');
							case FareFamilyOptionsType.Refundable:
								return tryTranslateWithFareKey(fareFamilyId, 'RefundOption');
							default:
								return '';
						}
					})
					.join(tryTranslateWithFareKey(fareFamilyId, 'And'));

			const upgradeOption: UpgradeOption = {
				canUpgradeTo: nextFare,
				optionsAvailableOnNextFare: availableOptionsOnNextFare,
				upgradeOffer
			};

			upgradeOptions.set(fareFamilyId, upgradeOption);
		}
	});

	return {
		...desktopFlightData,
		isModalOpen,
		openModal,
		getFareGroupTabHandler: handleModalOpen,
		closeModal,
		upgradeOptions,
		isComparisonOpen,
		setIsComparisonOpen
	};
};
